import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import {
  faAdd,
  faCircleNotch,
  faExternalLink,
  faPercentage,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, m } from "framer-motion";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { FlowVersion, FlowVersionStatus } from "src/api/flowTypes";
import { useCreateTrafficPolicy } from "src/api/queries";
import { Button } from "src/base-components/Button";
import { ExternalLink } from "src/base-components/ExternalLink";
import { Select } from "src/base-components/Select";
import { ProbabilisticPolicy } from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";

export type Props = {
  open: boolean;
  onClose: () => void;
  publishedVersions: FlowVersion[];
  currentTrafficPolicy: ProbabilisticPolicy;
  selectedVersion?: FlowVersion;
  flowId: string;
  afterLeave: () => void;
};

const getInitialTrafficPolicy = (
  currentTrafficPolicy: ProbabilisticPolicy,
  selectedVersion: FlowVersion | undefined,
) => {
  // Don't modify react query's copy
  const initialTrafficPolicy = structuredClone(currentTrafficPolicy);

  const addSelectedVersionToTrafficPolicy =
    selectedVersion?.status === FlowVersionStatus.PUBLISHED &&
    !initialTrafficPolicy.flow_versions.find(
      (v) => v.flow_version_id === selectedVersion.id,
    );

  if (addSelectedVersionToTrafficPolicy) {
    initialTrafficPolicy.flow_versions.push({
      flow_version_id: selectedVersion.id,
      percentage: 0,
    });
  }

  return initialTrafficPolicy;
};

export const ManageTrafficModal: React.FC<Props> = ({
  open,
  onClose,
  publishedVersions,
  currentTrafficPolicy,
  selectedVersion,
  flowId,
  afterLeave,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    reset: resetForm,
    formState,
    setFocus,
  } = useForm<ProbabilisticPolicy>({
    defaultValues: getInitialTrafficPolicy(
      currentTrafficPolicy,
      selectedVersion,
    ),
    reValidateMode: "onSubmit",
    resolver: (submittedPolicy) => {
      if (submittedPolicy.flow_versions.length === 0) {
        // Reset isDirty state so we disable
        // the submit button until the user
        // does anything
        resetForm({}, { keepValues: true });
        return {
          errors: {
            flow_versions: {
              message:
                "There should be at least one version to route traffic towards",
            },
          },
          values: {} as Record<string, never>,
        };
      }
      const percentageTotal = submittedPolicy.flow_versions.reduce(
        (prev, version) => prev + version.percentage,
        0,
      );
      if (percentageTotal !== 100) {
        resetForm({}, { keepValues: true });
        return {
          errors: {
            flow_versions: {
              message: "Traffic percentages should add up to 100%",
            },
          },
          values: {} as Record<string, never>,
        };
      } else {
        return { values: submittedPolicy, errors: {} as Record<string, never> };
      }
    },
  });
  const {
    fields: flowVersionFields,
    append: appendVersion,
    remove: removeVersion,
  } = useFieldArray({
    control,
    name: "flow_versions",
  });

  useEffect(() => {
    if (open) {
      const initialTrafficPolicy = getInitialTrafficPolicy(
        currentTrafficPolicy,
        selectedVersion,
      );
      resetForm(initialTrafficPolicy);
      const lastVersionIndex = initialTrafficPolicy.flow_versions.length - 1;
      setTimeout(() => {
        setFocus(`flow_versions.${lastVersionIndex}.percentage`);
      }, 0);
    }
    // Avoid changing the form while the modal fades away
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentVersionsIds = watch("flow_versions").map(
    (version) => version.flow_version_id,
  );

  const publishedVersionsWithoutTraffic = publishedVersions.filter(
    (version) => !currentVersionsIds.includes(version.id),
  );
  const onAddVersion = () => {
    const versionToAppend = publishedVersionsWithoutTraffic.at(0);

    if (versionToAppend) {
      const versionToAppendIndex = currentVersionsIds.length;
      appendVersion(
        { flow_version_id: versionToAppend.id, percentage: 0 },
        {
          focusName: `flow_versions.${versionToAppendIndex}.percentage`,
          shouldFocus: true,
        },
      );
    }
  };

  const createTrafficPolicy = useCreateTrafficPolicy();

  const onSubmit = async (submittedPolicy: ProbabilisticPolicy) => {
    await createTrafficPolicy.mutateAsync({
      createPayload: {
        flow_id: flowId,
        policy: submittedPolicy,
      },
      activate: true,
    });
    onClose();
  };

  const thereIsOnlyOneVersion = currentVersionsIds.length === 1;

  return (
    <Modal afterLeave={afterLeave} open={open} onClose={onClose}>
      <Modal.Header description="Route production traffic to different versions of your decision logic.">
        Manage traffic
      </Modal.Header>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          {formState.errors.flow_versions ? (
            <Callout type="error">
              {formState.errors.flow_versions.message}
            </Callout>
          ) : (
            <Callout type="info">
              Routing only applies when no version is specified in the API
              request
              <ExternalLink href="https://docs.taktile.com/versioning-and-deployment/traffic-routing">
                <Icon icon={faExternalLink} size="3xs" variant="active" />
              </ExternalLink>
            </Callout>
          )}
          <div className="decideScrollbar mt-6 h-80 pt-0.5 pl-0.5">
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {flowVersionFields.map((flowVersionField, i) => (
                  <m.div
                    key={flowVersionField.id}
                    animate={{
                      opacity: 1,
                      transition: {
                        type: "tween",
                        ease: "easeOut",
                        delay: 0.15,
                      },
                    }}
                    className="flex items-center"
                    initial={{ opacity: 0 }}
                    transition={{ type: "tween", ease: "easeOut" }}
                    layout
                  >
                    <Controller
                      control={control}
                      name={`flow_versions.${i}.flow_version_id`}
                      render={({ field: { onChange, value } }) => (
                        <FlowVersionSelector
                          publishedVersionsWithoutTraffic={
                            publishedVersionsWithoutTraffic
                          }
                          selectedVersion={
                            publishedVersions.find(
                              (version) => version.id === value,
                            )!
                          }
                          onChange={onChange}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`flow_versions.${i}.percentage`}
                      render={({ field: { onChange, value, ref } }) => (
                        <PercentageInput
                          ref={ref}
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                    <div
                      className={twJoin(
                        "ml-4",
                        thereIsOnlyOneVersion && "invisible",
                      )}
                    >
                      <IconButton
                        icon={faTrashAlt}
                        size="xs"
                        onClick={() => removeVersion(i)}
                      />
                    </div>
                  </m.div>
                ))}
              </AnimatePresence>
            </div>
            <m.div
              className="mt-2"
              transition={{ type: "tween", ease: "easeOut" }}
              layout
            >
              <Button
                dataLoc="policy-add-version-button"
                disabled={publishedVersionsWithoutTraffic.length === 0}
                iconLeft={faAdd}
                variant="secondary"
                onClick={onAddVersion}
              >
                Add version
              </Button>
            </m.div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="policy-save-button"
              disabled={formState.errors.flow_versions && !formState.isDirty}
              htmlType="submit"
              variant="primary"
              fullWidth
            >
              {formState.isSubmitting ? (
                <FontAwesomeIcon icon={faCircleNotch} width={12} spin />
              ) : (
                "Save"
              )}
            </Button>
          }
        />
      </form>
    </Modal>
  );
};

const PercentageInput = forwardRef<
  HTMLInputElement,
  {
    value: number;
    onChange: (value: number) => void;
  }
>(({ value, onChange }, ref) => {
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current!);
  return (
    <div className="relative w-max">
      <div
        className="absolute top-1/2 right-3 -translate-y-1/2 font-inter-normal-13px text-gray-500"
        onClick={() => {
          innerRef.current?.focus();
        }}
      >
        <Icon icon={faPercentage} size="xs" />
      </div>
      <input
        ref={innerRef}
        className="h-8 w-[90px] rounded-lg border border-gray-300 pr-8 pl-4 font-inter-normal-13px text-gray-800 outline-1 outline-indigo-500 -outline-offset-1 [appearance:textfield] focus:outline [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        data-loc="percentage-input"
        max="100"
        min="0"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
    </div>
  );
});

const FlowVersionSelector: React.FC<{
  selectedVersion: FlowVersion;
  onChange: (id: string) => void;
  publishedVersionsWithoutTraffic: FlowVersion[];
}> = ({ selectedVersion, onChange, publishedVersionsWithoutTraffic }) => {
  const versionNameToOption = (versionName?: string, isSelected = false) => {
    return (
      <div
        className={twJoin(
          "min-w-0 flex-1 truncate",
          isSelected ? "mr-3" : "mr-8",
        )}
      >
        {versionName}
      </div>
    );
  };
  const options = publishedVersionsWithoutTraffic
    .map((v) => ({ key: v.id, value: versionNameToOption(v.name) }))
    .concat([
      {
        key: selectedVersion.id,
        value: versionNameToOption(selectedVersion.name, true),
      },
    ]);
  return (
    <div className="relative mr-4 min-w-0 flex-1">
      <Select
        options={options}
        value={selectedVersion.id}
        onChange={onChange}
      />
    </div>
  );
};
